import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, MenuItem, Snackbar, Stack, Typography } from '@mui/material';
import GlassFormField from '@/components/design-studio/GlassFormField';
import { MarketingFormPanel } from '@/components/design-studio/GlassFormScrollPanel';
import { areRequiredFieldsFilled, canSubmitRequiredForm } from '@/utils/required-fields';
import {
  getPostalCodeLabel,
  getStateProvinceLabel,
  getStateProvinceOptions,
} from '@/utils/region-options';
import {
  FORM_PAGE_DESKTOP_BP,
  GLASS_FORM_PAIR_STACK_SPACING,
  GLASS_FORM_PANEL_PADDING_SX,
  GLASS_FORM_ROOT_SX,
  GLASS_FORM_ROW_SX,
  LIQUID_GLASS_FORM_SX,
  LIQUID_GLASS_PANEL_SX,
  LIQUID_GLASS_SUBMIT_BUTTON_SX,
} from '@/components/design-studio/design-studio-glass';

const ROLE_OPTIONS = [
  { value: 'designer', label: 'Designer' },
  { value: 'architect', label: 'Architect' },
  { value: 'developer', label: 'Developer' },
  { value: 'dealer', label: 'Dealer' },
  { value: 'end_user', label: 'End User' },
  { value: 'other', label: 'Other' },
];

const COUNTRY_OPTIONS = [
  { value: 'Canada', label: 'Canada' },
  { value: 'USA', label: 'USA' },
];

const PART1_REQUIRED_KEYS = [
  'dealer_name',
  'email',
  'phone',
  'address_1',
  'country',
  'city',
  'state_province',
  'zip_postal_code',
  'role',
];

const PART2_REQUIRED_KEYS = [
  'years_in_business',
  'showroom_sq_ft',
  'display_count',
  'lines_carried_count',
  'niico_display_count_expected',
  'sales_staff_count',
  'annual_sales_volume',
  'interest_reason',
];

const PART1_INITIAL = {
  dealer_name: '',
  email: '',
  phone: '',
  address_1: '',
  address_2: '',
  country: 'Canada',
  city: '',
  state_province: '',
  zip_postal_code: '',
  role: '',
};

const PART2_INITIAL = {
  years_in_business: '',
  showroom_sq_ft: '',
  display_count: '',
  lines_carried_count: '',
  niico_display_count_expected: '',
  sales_staff_count: '',
  annual_sales_volume: '',
  interest_reason: '',
};

function mapFieldErrors(payload) {
  if (!payload || typeof payload !== 'object') return {};
  const next = {};
  Object.keys(payload).forEach(key => {
    const v = payload[key];
    if (Array.isArray(v) && v.length) next[key] = String(v[0]);
    else if (typeof v === 'string') next[key] = v;
  });
  return next;
}

function canSubmitPart2(values) {
  if (!areRequiredFieldsFilled(values, PART2_REQUIRED_KEYS)) return false;
  return String(values.interest_reason).length <= 500;
}

function part2Payload(values) {
  return {
    years_in_business: Number(values.years_in_business),
    showroom_sq_ft: Number(values.showroom_sq_ft),
    display_count: Number(values.display_count),
    lines_carried_count: Number(values.lines_carried_count),
    niico_display_count_expected: Number(values.niico_display_count_expected),
    sales_staff_count: Number(values.sales_staff_count),
    annual_sales_volume: Number(values.annual_sales_volume),
    interest_reason: String(values.interest_reason).trim(),
  };
}

async function parseJsonResponse(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export default function TradePartnerForm({ variant = 'default' }) {
  const isGlass = variant === 'glass';
  const [step, setStep] = React.useState(1);
  const [applicationId, setApplicationId] = React.useState(null);
  const [part1Values, setPart1Values] = React.useState(PART1_INITIAL);
  const [part2Values, setPart2Values] = React.useState(PART2_INITIAL);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const [snack, setSnack] = React.useState({
    open: false,
    message: '',
    severity: 'error',
  });

  const closeSnack = React.useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setSnack(prev => ({ ...prev, open: false }));
  }, []);

  const openErrorSnack = React.useCallback(message => {
    setSnack({
      open: true,
      message: message || 'Something went wrong.',
      severity: 'error',
    });
  }, []);

  const handlePart1Change = React.useCallback(e => {
    const { name, value } = e.target;
    setPart1Values(prev => {
      if (name === 'country' && value !== prev.country) {
        return { ...prev, country: value, state_province: '' };
      }
      return { ...prev, [name]: value };
    });
  }, []);

  const stateProvinceOptions = React.useMemo(
    () => getStateProvinceOptions(part1Values.country),
    [part1Values.country],
  );

  const stateProvinceLabel = React.useMemo(
    () => getStateProvinceLabel(part1Values.country),
    [part1Values.country],
  );

  const postalCodeLabel = React.useMemo(
    () => getPostalCodeLabel(part1Values.country),
    [part1Values.country],
  );

  const handlePart2Change = React.useCallback(e => {
    const { name, value } = e.target;
    setPart2Values(prev => ({ ...prev, [name]: value }));
  }, []);

  const canGoNext = React.useMemo(
    () => canSubmitRequiredForm(part1Values, PART1_REQUIRED_KEYS, ['email']),
    [part1Values],
  );

  const canSubmitStep2 = React.useMemo(() => canSubmitPart2(part2Values), [part2Values]);

  const handleApiError = React.useCallback(
    (res, payload) => {
      if (payload && typeof payload === 'object') {
        const nextFieldErrors = mapFieldErrors(payload);
        setFieldErrors(nextFieldErrors);
        const detailMsg = typeof payload.detail === 'string' ? payload.detail : '';
        openErrorSnack(detailMsg || 'Please fix the highlighted fields.');
      } else {
        openErrorSnack('Submission failed. Please try again.');
      }
    },
    [openErrorSnack],
  );

  const handleNext = React.useCallback(async () => {
    setIsSubmitting(true);
    setFieldErrors({});

    try {
      const url = applicationId
        ? `/t/public/trade-partner/${applicationId}/`
        : '/t/public/trade-partner/';
      const res = await fetch(url, {
        method: applicationId ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(part1Values),
      });

      const payload = await parseJsonResponse(res);
      if (!res.ok) {
        handleApiError(res, payload);
        return;
      }

      const id = payload?.id ?? applicationId;
      if (id != null) setApplicationId(id);
      setStep(2);
    } catch {
      openErrorSnack('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [applicationId, part1Values, handleApiError, openErrorSnack]);

  const handleSubmit = React.useCallback(
    async e => {
      e.preventDefault();
      if (!applicationId) {
        openErrorSnack('Please complete step 1 first.');
        setStep(1);
        return;
      }

      setIsSubmitting(true);
      setFieldErrors({});

      try {
        const res = await fetch(`/t/public/trade-partner/${applicationId}/showroom/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(part2Payload(part2Values)),
        });

        const payload = await parseJsonResponse(res);
        if (!res.ok) {
          handleApiError(res, payload);
          return;
        }

        setSubmitted(true);
        setStep(1);
        setApplicationId(null);
        setPart1Values(PART1_INITIAL);
        setPart2Values(PART2_INITIAL);
      } catch {
        openErrorSnack('Network error. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [applicationId, part2Values, handleApiError, openErrorSnack],
  );

  const handleBack = React.useCallback(() => {
    setFieldErrors({});
    setStep(1);
  }, []);

  const panelSx = isGlass
    ? { ...LIQUID_GLASS_PANEL_SX, ...GLASS_FORM_PANEL_PADDING_SX }
    : { p: 2.5, borderRadius: 2, border: 1, borderColor: 'divider' };

  const rowSx = isGlass ? GLASS_FORM_ROW_SX : { mb: 2 };
  const rowSpacing = isGlass ? GLASS_FORM_PAIR_STACK_SPACING : { xs: 0, [FORM_PAGE_DESKTOP_BP]: 2 };

  const sectionHeadingSx = isGlass
    ? {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        mb: { xs: 1, [FORM_PAGE_DESKTOP_BP]: 0.25 },
      }
    : { mb: 2 };

  const buttonSx = isGlass
    ? { ...LIQUID_GLASS_SUBMIT_BUTTON_SX, py: 1, minWidth: 120 }
    : { borderRadius: 2, py: 1.25, minWidth: 140 };

  const numberFieldProps = {
    type: 'number',
    inputProps: { min: 0, step: 1 },
  };

  const rootSx = isGlass ? GLASS_FORM_ROOT_SX : { width: '100%' };

  return (
    <Box sx={rootSx}>
      <Stack spacing={2} sx={isGlass ? { flex: 1, minHeight: 0 } : undefined}>
        {submitted && (
          <Alert
            severity="success"
            sx={
              isGlass
                ? {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'common.white',
                    border: '1px solid rgba(255, 255, 255, 0.35)',
                    backdropFilter: 'blur(12px)',
                    '& .MuiAlert-icon': { color: 'common.white' },
                  }
                : undefined
            }
          >
            Thank you for your application. Our team will be in touch soon.
          </Alert>
        )}

        {!submitted && step === 1 && (
          <MarketingFormPanel
            isGlass={isGlass}
            panelSx={panelSx}
            formSx={isGlass ? LIQUID_GLASS_FORM_SX : undefined}
            onSubmit={e => {
              e.preventDefault();
              handleNext();
            }}
            header={
              <Box sx={rowSx}>
                <Typography
                  component="h2"
                  variant={isGlass ? undefined : 'h6'}
                  fontWeight={600}
                  sx={sectionHeadingSx}
                >
                  Dealer Contact Information
                </Typography>
              </Box>
            }
            footer={
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: isGlass ? 0.25 : 0 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size={isGlass ? 'medium' : 'large'}
                  disableElevation
                  disabled={isSubmitting || !canGoNext}
                  sx={buttonSx}
                >
                  {isSubmitting ? 'Saving...' : 'Next'}
                </Button>
              </Box>
            }
          >
            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="dealer_name"
                label="Name Of Dealer"
                value={part1Values.dealer_name}
                onChange={handlePart1Change}
                fullWidth
                required
                error={Boolean(fieldErrors.dealer_name)}
                helperText={fieldErrors.dealer_name}
                autoComplete="organization"
              />
            </Box>

            <Stack
              direction={{ xs: 'column', [FORM_PAGE_DESKTOP_BP]: 'row' }}
              spacing={rowSpacing}
              sx={rowSx}
            >
              <GlassFormField
                isGlass={isGlass}
                name="email"
                label="Email"
                type="email"
                value={part1Values.email}
                onChange={handlePart1Change}
                fullWidth
                required
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email}
                autoComplete="email"
              />
              <GlassFormField
                isGlass={isGlass}
                name="phone"
                label="Phone"
                value={part1Values.phone}
                onChange={handlePart1Change}
                fullWidth
                required
                error={Boolean(fieldErrors.phone)}
                helperText={fieldErrors.phone}
                autoComplete="tel"
              />
            </Stack>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="address_1"
                label="Address 1"
                value={part1Values.address_1}
                onChange={handlePart1Change}
                fullWidth
                required
                error={Boolean(fieldErrors.address_1)}
                helperText={fieldErrors.address_1}
                autoComplete="address-line1"
              />
            </Box>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="address_2"
                label="Address 2 (Optional)"
                value={part1Values.address_2}
                onChange={handlePart1Change}
                fullWidth
                error={Boolean(fieldErrors.address_2)}
                helperText={fieldErrors.address_2}
                autoComplete="address-line2"
              />
            </Box>

            <Stack
              direction={{ xs: 'column', [FORM_PAGE_DESKTOP_BP]: 'row' }}
              spacing={rowSpacing}
              sx={rowSx}
            >
              <GlassFormField
                isGlass={isGlass}
                name="country"
                label="Country"
                value={part1Values.country}
                onChange={handlePart1Change}
                fullWidth
                select
                required
                error={Boolean(fieldErrors.country)}
                helperText={fieldErrors.country}
              >
                {COUNTRY_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </GlassFormField>
              <GlassFormField
                isGlass={isGlass}
                name="state_province"
                label={stateProvinceLabel}
                value={part1Values.state_province}
                onChange={handlePart1Change}
                fullWidth
                select
                required
                error={Boolean(fieldErrors.state_province)}
                helperText={fieldErrors.state_province}
              >
                <MenuItem value="" disabled>
                  Select one
                </MenuItem>
                {stateProvinceOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </GlassFormField>
            </Stack>

            <Stack
              direction={{ xs: 'column', [FORM_PAGE_DESKTOP_BP]: 'row' }}
              spacing={rowSpacing}
              sx={rowSx}
            >
              <GlassFormField
                isGlass={isGlass}
                name="city"
                label="City"
                value={part1Values.city}
                onChange={handlePart1Change}
                fullWidth
                required
                error={Boolean(fieldErrors.city)}
                helperText={fieldErrors.city}
                autoComplete="address-level2"
              />
              <GlassFormField
                isGlass={isGlass}
                name="zip_postal_code"
                label={postalCodeLabel}
                value={part1Values.zip_postal_code}
                onChange={handlePart1Change}
                fullWidth
                required
                error={Boolean(fieldErrors.zip_postal_code)}
                helperText={fieldErrors.zip_postal_code}
                autoComplete="postal-code"
              />
            </Stack>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="role"
                label="I am a"
                value={part1Values.role}
                onChange={handlePart1Change}
                fullWidth
                select
                required
                error={Boolean(fieldErrors.role)}
                helperText={fieldErrors.role}
              >
                <MenuItem value="" disabled>
                  Select one
                </MenuItem>
                {ROLE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </GlassFormField>
            </Box>
          </MarketingFormPanel>
        )}

        {!submitted && step === 2 && (
          <MarketingFormPanel
            isGlass={isGlass}
            panelSx={panelSx}
            formSx={isGlass ? LIQUID_GLASS_FORM_SX : undefined}
            onSubmit={handleSubmit}
            header={
              <Box sx={rowSx}>
                <Typography
                  component="h2"
                  variant={isGlass ? undefined : 'h6'}
                  fontWeight={600}
                  sx={sectionHeadingSx}
                >
                  Showroom &amp; Business Details
                </Typography>
              </Box>
            }
            footer={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  mt: isGlass ? 0.25 : 0,
                }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  size={isGlass ? 'medium' : 'large'}
                  onClick={handleBack}
                  disabled={isSubmitting}
                  sx={
                    isGlass
                      ? {
                          color: 'common.white',
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          '&:hover': { borderColor: 'common.white' },
                        }
                      : undefined
                  }
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size={isGlass ? 'medium' : 'large'}
                  disableElevation
                  disabled={isSubmitting || !canSubmitStep2}
                  sx={buttonSx}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </Box>
            }
          >
            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="years_in_business"
                label="How many years has your company been in business?"
                value={part2Values.years_in_business}
                onChange={handlePart2Change}
                fullWidth
                required
                {...numberFieldProps}
                error={Boolean(fieldErrors.years_in_business)}
                helperText={fieldErrors.years_in_business}
              />
            </Box>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="showroom_sq_ft"
                label="What is the square footage of your showroom?"
                value={part2Values.showroom_sq_ft}
                onChange={handlePart2Change}
                fullWidth
                required
                {...numberFieldProps}
                error={Boolean(fieldErrors.showroom_sq_ft)}
                helperText={fieldErrors.showroom_sq_ft}
              />
            </Box>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="display_count"
                label="How many displays do you have in your showroom?"
                value={part2Values.display_count}
                onChange={handlePart2Change}
                fullWidth
                required
                {...numberFieldProps}
                error={Boolean(fieldErrors.display_count)}
                helperText={fieldErrors.display_count}
              />
            </Box>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="lines_carried_count"
                label="How many different lines do you carry?"
                value={part2Values.lines_carried_count}
                onChange={handlePart2Change}
                fullWidth
                required
                {...numberFieldProps}
                error={Boolean(fieldErrors.lines_carried_count)}
                helperText={fieldErrors.lines_carried_count}
              />
            </Box>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="niico_display_count_expected"
                label="How many NIICO displays do you expect to have in your showroom?"
                value={part2Values.niico_display_count_expected}
                onChange={handlePart2Change}
                fullWidth
                required
                {...numberFieldProps}
                error={Boolean(fieldErrors.niico_display_count_expected)}
                helperText={fieldErrors.niico_display_count_expected}
              />
            </Box>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="sales_staff_count"
                label="How many sales/design staff do you have in your showroom?"
                value={part2Values.sales_staff_count}
                onChange={handlePart2Change}
                fullWidth
                required
                {...numberFieldProps}
                error={Boolean(fieldErrors.sales_staff_count)}
                helperText={fieldErrors.sales_staff_count}
              />
            </Box>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="annual_sales_volume"
                label="What is your total annual sales volume?"
                value={part2Values.annual_sales_volume}
                onChange={handlePart2Change}
                fullWidth
                required
                {...numberFieldProps}
                error={Boolean(fieldErrors.annual_sales_volume)}
                helperText={fieldErrors.annual_sales_volume}
              />
            </Box>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="interest_reason"
                label="Why are you interested in carrying the NIICO product?"
                value={part2Values.interest_reason}
                onChange={handlePart2Change}
                fullWidth
                required
                multiline
                minRows={2}
                maxRows={4}
                inputProps={{ maxLength: 500 }}
                error={Boolean(fieldErrors.interest_reason)}
                helperText={
                  fieldErrors.interest_reason || `${String(part2Values.interest_reason).length}/500`
                }
              />
            </Box>
          </MarketingFormPanel>
        )}

        <Snackbar open={snack.open} autoHideDuration={6000} onClose={closeSnack}>
          <Alert
            onClose={closeSnack}
            severity={snack.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Stack>
    </Box>
  );
}

TradePartnerForm.propTypes = {
  variant: PropTypes.oneOf(['default', 'glass']),
};
